<template>
  <div class="port-mapping-container">
    <!-- 服务器信息卡片 -->
    <div class="settings-card">
      <div class="card-header">
        <h3 class="card-title">{{ $t('serverMapping.title') }}</h3>
        <p class="card-subtitle">
          <span v-if="flagChooseServer">{{ ipAddr }}</span>
          <router-link v-else to="/transfer-server" class="text-danger">
            {{ $t('serverMapping.pleaseSelectTransferServer') }}
          </router-link>
        </p>
      </div>
      <div class="card-body">
        <p class="description-text">
          {{ $t('serverMapping.description') }}
        </p>
      </div>
    </div>

    <!-- 端口映射列表卡片 -->
    <div class="settings-card">
      <div class="card-header">
        <div class="header-content">
          <h3 class="card-title">{{ $t('serverMapping.mappingList') }}</h3>
          <div class="btn-group">
            <el-button type="info" size="small" @click="showLogDialog">
              <el-icon><Document /></el-icon>
              {{ $t('serverMapping.viewLogs') }}
            </el-button>
            <el-button type="primary" size="small" @click="showAddDialog">{{ $t('serverMapping.add') }}</el-button>
            <el-button type="warning" size="small" @click="stopAll">{{ $t('serverMapping.stopAll') }}</el-button>
            <el-button type="success" size="small" @click="restartAll">{{ $t('serverMapping.restartAll') }}</el-button>
            <el-button type="info" size="small" @click="refreshData">{{ $t('serverMapping.refresh') }}</el-button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div v-if="isLoading" class="loading-overlay">
          <el-icon class="is-loading"><Loading /></el-icon>
          {{ $t('serverMapping.loading') }}
        </div>

        <div v-if="!isLoading && mappingList.length === 0" class="empty-state">
          <el-icon><Box /></el-icon>
          <p>{{ $t('serverMapping.noMappingConfig') }}</p>
        </div>

        <div v-if="mappingList.length > 0" class="table-responsive">
          <table class="mapping-table">
            <thead>
              <tr>
                <th>{{ $t('serverMapping.name') }}</th>
                <th>{{ $t('serverMapping.internalAddress') }}</th>
                <th>{{ $t('serverMapping.internalPort') }}</th>
                <th>{{ $t('serverMapping.mappingPort') }}</th>
                <th>{{ $t('serverMapping.status') }}</th>
                <th>{{ $t('serverMapping.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in mappingList" :key="item.name" :class="{ 'stopped-row': !isRunning(item.result) }">
                <td :data-label="$t('serverMapping.name')">{{ item.name }}</td>
                <td :data-label="$t('serverMapping.internalAddress')">{{ item.targetIP }}</td>
                <td :data-label="$t('serverMapping.internalPort')">{{ item.targetPort }}</td>
                <td :data-label="$t('serverMapping.mappingPort')">{{ item.mappingPort }}</td>
                <td data-label="状态" class="status-cell">
                  <div class="status-content" :class="getStatusClass(item.result)">
                    {{ item.result }}
                  </div>
                </td>
                <td data-label="操作" class="action-cell">
                  <div class="action-buttons">
                    <!-- 只在未运行状态显示编辑按钮 -->
                    <el-button
                      v-if="!isRunning(item.result)"
                      type="primary"
                      size="small"
                      @click="showEditDialog(item)"
                    >
                      {{ $t('serverMapping.edit') }}
                    </el-button>
                    <el-button
                      v-if="!isRunning(item.result)"
                      type="success"
                      size="small"
                      @click="startMapping(item.name)"
                    >
                      {{ $t('serverMapping.start') }}
                    </el-button>
                    <el-button
                      v-else
                      type="warning"
                      size="small"
                      @click="stopMapping(item.name)"
                    >
                      {{ $t('serverMapping.stop') }}
                    </el-button>
                    <el-button
                      type="danger"
                      size="small"
                      @click="deleteMapping(item.name)"
                    >
                      {{ $t('serverMapping.delete') }}
                    </el-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 新增/编辑映射对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEditing ? $t('serverMapping.editMapping') : $t('serverMapping.addMapping')"
      :width="isMobile ? '100%' : '600px'"
      :before-close="handleCloseDialog"
      :destroy-on-close="true"
      class="mapping-dialog"
    >
      <el-form
        :model="mappingForm"
        :rules="formRules"
        ref="mappingFormRef"
        label-position="top"
      >
        <el-form-item :label="$t('serverMapping.name')" prop="name">
          <el-input
            v-model="mappingForm.name"
            :placeholder="$t('serverMapping.namePlaceholder')"
            :disabled="isEditing"
          />
        </el-form-item>

        <el-form-item :label="$t('serverMapping.internalAddress')" prop="targetIP">
          <el-input v-model="mappingForm.targetIP" :placeholder="$t('serverMapping.internalIpPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('serverMapping.internalPort')" prop="targetPort">
          <el-input
            v-model.number="mappingForm.targetPort"
            type="number"
            :placeholder="$t('serverMapping.internalPortPlaceholder')"
          />
        </el-form-item>

        <el-form-item :label="$t('serverMapping.mappingPort')" prop="mappingPort">
          <el-input
            v-model.number="mappingForm.mappingPort"
            type="number"
            :placeholder="$t('serverMapping.mappingPortPlaceholder')"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ $t('serverMapping.cancel') }}</el-button>
          <el-button type="primary" @click="submitMappingForm">{{ $t('serverMapping.save') }}</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 端口映射日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('serverMapping.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('serverMapping.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('serverMapping.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('serverMapping.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
            :icon="Download"
          >
            {{ $t('serverMapping.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
            :icon="Refresh"
          >
            {{ $t('serverMapping.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('serverMapping.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('serverMapping.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('serverMapping.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Loading, Box, Document, InfoFilled } from '@element-plus/icons-vue'
import { API_BASE_URL } from "@/config";
import { useRouter } from 'vue-router'

export default {
  name: 'PortMapping',
  components: { Loading, Box, Document, InfoFilled },
  setup() {
    const { t: i18n_t } = useI18n()
    const router = useRouter()
    const mappingList = ref([])
    const isLoading = ref(false)
    const intervalId = ref(null)
    const dialogVisible = ref(false)
    const isEditing = ref(false)
    const mappingFormRef = ref(null)

    const isMobile = ref(false)

    // 日志相关
    const logDialogVisible = ref(false)
    const logLoading = ref(false)
    const logClearing = ref(false)
    const logDownloading = ref(false)
    const logsData = ref([])
    const logTotal = ref(0)
    const logCurrentPage = ref(1)
    const logPageSize = ref(50)
    const logContentRef = ref(null)
    const autoRefreshEnabled = ref(true)
    const logRefreshInterval = ref(null)

    // 检测移动端
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768
    }

    
    // 表单数据
    const mappingForm = ref({
      name: '',
      targetIP: '127.0.0.1',
      targetPort: '',
      mappingPort: ''
    })
    
    // 页面状态
    const flagChooseServer = ref(false)
    const ipAddr = ref('')
    
    // 表单验证规则
    const formRules = {
      name: [
        { required: true, message: i18n_t('serverMapping.nameRequired'), trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9]{3,15}$/, message: i18n_t('serverMapping.nameFormatError'), trigger: 'blur' }
      ],
      targetIP: [
        { required: true, message: i18n_t('serverMapping.ipRequired'), trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            const exp = /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/;
            if (exp.test(value)) {
              callback()
            } else {
              callback(new Error(i18n_t('serverMapping.ipFormatError')))
            }
          },
          trigger: 'blur'
        }
      ],
      targetPort: [
        { required: true, message: i18n_t('serverMapping.internalPortRequired'), trigger: 'blur' },
        { type: 'number', min: 1, max: 65535, message: i18n_t('serverMapping.internalPortRangeError'), trigger: 'blur' }
      ],
      mappingPort: [
        { required: true, message: i18n_t('serverMapping.mappingPortRequired'), trigger: 'blur' },
        { type: 'number', min: 0, max: 65535, message: i18n_t('serverMapping.mappingPortRangeError'), trigger: 'blur' }
      ]
    }
    
    // 检查服务是否运行中
    const isRunning = (status) => {
      return status && !status.includes('stopped') && !status.includes('未启动') && !status.includes('not connect')
    }

    // 获取状态样式类
    const getStatusClass = (status) => {
      if (!status) return 'stopped'

      const lowerStatus = status.toLowerCase()

      if (lowerStatus.includes('failed') || lowerStatus.includes('失败')) {
        return 'failed'
      } else if (lowerStatus.includes('success') || lowerStatus.includes('running') || lowerStatus.includes('运行中')) {
        return 'running'
      } else {
        return 'stopped'
      }
    }

    // 显示新增对话框
    const showAddDialog = () => {
      if (!flagChooseServer.value) {
        ElMessage.warning(i18n_t('serverMapping.pleaseSelectServer'))
        return
      }

      isEditing.value = false
      mappingForm.value = {
        name: '',
        targetIP: '127.0.0.1',
        targetPort: '',
        mappingPort: ''
      }
      dialogVisible.value = true
    }

    // 显示编辑对话框
    const showEditDialog = (item) => {
      if (isRunning(item.result)) {
        ElMessage.warning(i18n_t('serverMapping.pleaseStopFirst'))
        return
      }

      isEditing.value = true
      mappingForm.value = {
        name: item.name,
        targetIP: item.targetIP,
        targetPort: item.targetPort,
        mappingPort: item.mappingPort
      }
      dialogVisible.value = true
    }

    // 关闭对话框前的确认
    const handleCloseDialog = (done) => {
      ElMessageBox.confirm(i18n_t('serverMapping.confirmDiscard'), i18n_t('serverMapping.confirm'), {
        confirmButtonText: i18n_t('serverMapping.confirm'),
        cancelButtonText: i18n_t('serverMapping.cancelText'),
        type: 'warning'
      }).then(() => {
        done()
      }).catch(() => {})
    }

    // 提交表单
    const submitMappingForm = async () => {
      try {
        await mappingFormRef.value.validate()

        const url = isEditing.value ? '/update-mapping' : '/mapping'
        const method = 'POST'

        const params = new URLSearchParams()
        params.append('name', mappingForm.value.name)
        params.append('targetIP', mappingForm.value.targetIP)
        params.append('targetPort', mappingForm.value.targetPort)
        params.append('mappingPort', mappingForm.value.mappingPort)

        const response = await fetch(`${API_BASE_URL}${url}`, {
          method,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('serverMapping.operationFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || i18n_t('serverMapping.operationSuccess'))
        dialogVisible.value = false
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('serverMapping.operationFailed'))
      }
    }

    // 启动映射
    const startMapping = async (name) => {
      try {
        const params = new URLSearchParams()
        params.append('name', name)
        params.append('flag', 'off')

        const response = await fetch(`${API_BASE_URL}/restart-mapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('serverMapping.operationFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || i18n_t('serverMapping.startSuccess'))
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('serverMapping.startFailed'))
      }
    }

    // 停止映射
    const stopMapping = async (name) => {
      try {
        const params = new URLSearchParams()
        params.append('name', name)
        params.append('flag', 'off')

        const response = await fetch(`${API_BASE_URL}/stop-mapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: params
        })

        if (!response.ok) {
          throw new Error(i18n_t('serverMapping.operationFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || i18n_t('serverMapping.stopSuccess'))
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('serverMapping.stopFailed'))
      }
    }

    // 删除映射
    const deleteMapping = (name) => {
      ElMessageBox.confirm(i18n_t('serverMapping.deleteConfirm', { name }), i18n_t('serverMapping.confirm'), {
        confirmButtonText: i18n_t('serverMapping.confirm'),
        cancelButtonText: i18n_t('serverMapping.cancelText'),
        type: 'warning'
      }).then(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/mapping?name=${name}`, {
            method: 'DELETE',
            credentials: 'include'
          })

          if (!response.ok) {
            throw new Error(i18n_t('serverMapping.deleteFailed'))
          }

          const data = await response.json()
          ElMessage.success(data.errMsg || i18n_t('serverMapping.deleteSuccess'))
          fetchData()
        } catch (error) {
          ElMessage.error(error.message || i18n_t('serverMapping.deleteFailed'))
        }
      }).catch(() => {})
    }

    // 停止所有映射
    const stopAll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/stop-mapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: 'flag=on'
        })

        if (!response.ok) {
          throw new Error(i18n_t('serverMapping.stopAllFailed'))
        }

        const data = await response.json()
        ElMessage.success(data.errMsg || i18n_t('serverMapping.stopAllSuccess'))
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('serverMapping.stopAllFailed'))
      }
    }

    // 重启所有映射
    const restartAll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/restart-mapping`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          credentials: 'include',
          body: 'flag=on'
        })

        if (!response.ok) {
          throw new Error(i18n_t('serverMapping.restartAllFailed'))
        }

        const data = await response.json()
        if (data.errMsg.includes("not select server")) {
          ElMessage.warning(i18n_t('serverMapping.pleaseSelectServer'))
        } else {
          ElMessage.success(data.errMsg || i18n_t('serverMapping.restartAllSuccess'))
        }
        fetchData()
      } catch (error) {
        ElMessage.error(error.message || i18n_t('serverMapping.restartAllFailed'))
      }
    }
    
    // 刷新数据
    const refreshData = () => {
      fetchData()
    }

    // 获取数据
    const fetchData = async () => {
      try {
        isLoading.value = true
        const response = await fetch(`${API_BASE_URL}/mapping-list`, {
          method: 'GET',
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(i18n_t('serverMapping.fetchDataFailed'))
        }

        const result = await response.json()

        if (result.errCode === 1000) {
          ElMessage.error(i18n_t('serverMapping.pleaseLoginFirst'))
          router.push('/user-login?callback=server-mapping')
          return
        }

        if (result.errCode != 0) {
          ElMessage.error(result.errMsg)
          return
        }

        mappingList.value = result.data || []
        flagChooseServer.value = result.flagChooseServer || false
        ipAddr.value = result.ipAddr || ''

      } catch (error) {
        console.error(i18n_t('serverMapping.fetchDataFailed') + ':', error)
        ElMessage.error(error.message || i18n_t('serverMapping.fetchDataFailed'))
        mappingList.value = []
      } finally {
        isLoading.value = false
      }
    }

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true
      fetchLogs()
      // 开启日志自动刷新（如果开启状态）
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh()
      }
    }

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh()
        ElMessage.success(i18n_t('serverMapping.autoRefreshEnabled'))
      } else {
        stopLogAutoRefresh()
        ElMessage.info(i18n_t('serverMapping.autoRefreshDisabled'))
      }
    }

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh() // 先停止已有的定时器
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false) // 静默刷新，不显示加载状态
        }
      }, 5000)
    }

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value)
        logRefreshInterval.value = null
      }
    }

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true
        }

        const params = new URLSearchParams({
          module: 'mapping',
          page: logCurrentPage.value,
          size: logPageSize.value
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('serverMapping.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('serverMapping.pleaseLoginFirst'))
          router.push('/user-login?callback=server-mapping')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('serverMapping.fetchLogsFailed'))
        }

        logsData.value = data.data || []
        logTotal.value = data.count || 0

        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0
        }
      } catch (error) {
        console.error(i18n_t('serverMapping.fetchLogsError') + ':', error)
        if (showLoading) {
          ElMessage.error(i18n_t('serverMapping.fetchLogsError') + `: ${error.message || i18n_t('serverMapping.unknownError')}`)
        }
      } finally {
        if (showLoading) {
          logLoading.value = false
        }
      }
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true)
    }

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(i18n_t('serverMapping.confirmClearLogs'), i18n_t('serverMapping.confirm'), {
          confirmButtonText: i18n_t('serverMapping.confirm'),
          cancelButtonText: i18n_t('serverMapping.cancelText'),
          type: 'warning'
        })

        logClearing.value = true

        const params = new URLSearchParams({
          module: 'mapping'
        })

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('serverMapping.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('serverMapping.pleaseLoginFirst'))
          router.push('/user-login?callback=server-mapping')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('serverMapping.clearLogsFailed'))
        }

        ElMessage.success(i18n_t('serverMapping.logsCleared'))
        fetchLogs(true)
      } catch (error) {
        if (error !== 'cancel') {
          console.error(i18n_t('serverMapping.clearLogsFailed') + ':', error)
          ElMessage.error(i18n_t('serverMapping.clearLogsFailed') + `: ${error.message || i18n_t('serverMapping.unknownError')}`)
        }
      } finally {
        logClearing.value = false
      }
    }

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true

        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'mapping',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        })

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        })

        if (!response.ok) throw new Error(i18n_t('serverMapping.networkError'))

        const data = await response.json()

        if (data.errCode === 1000) {
          ElMessage.error(i18n_t('serverMapping.pleaseLoginFirst'))
          router.push('/user-login?callback=server-mapping')
          return
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || i18n_t('serverMapping.fetchLogsFailed'))
        }

        const allLogs = data.data || []

        if (allLogs.length === 0) {
          ElMessage.warning(i18n_t('serverMapping.noLogsToDownload'))
          return
        }

        // 格式化所有日志内容
        let logContent = ''
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`
        })

        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `port_mapping_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        ElMessage.success(i18n_t('serverMapping.downloadSuccess', { count: allLogs.length }))
      } catch (error) {
        console.error(i18n_t('serverMapping.downloadFailed') + ':', error)
        ElMessage.error(i18n_t('serverMapping.downloadFailed') + `: ${error.message || i18n_t('serverMapping.unknownError')}`)
      } finally {
        logDownloading.value = false
      }
    }

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size
      logCurrentPage.value = 1
      fetchLogs(true)
    }

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page
      fetchLogs(true)
    }

    // 窗口大小变化处理
    const handleResize = () => {
      checkMobile()
    }

    // 初始化数据
    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', handleResize)
      fetchData()

      // 每5秒刷新一次数据
      intervalId.value = setInterval(() => {
        fetchData()
      }, 5000)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }
      stopLogAutoRefresh()
    })

    return {
      isMobile,
      mappingList,
      isLoading,
      dialogVisible,
      isEditing,
      mappingForm,
      mappingFormRef,
      formRules,
      flagChooseServer,
      ipAddr,
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      autoRefreshEnabled,
      showAddDialog,
      showEditDialog,
      handleCloseDialog,
      submitMappingForm,
      startMapping,
      stopMapping,
      deleteMapping,
      stopAll,
      restartAll,
      refreshData,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange,
      isRunning,
      getStatusClass
    }
  }
}
</script>

<style scoped lang="scss">
.port-mapping-container {
  padding: 0px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 12px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0 8px;
  }
}

.settings-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  .card-header {
    padding: 20px;
    border-bottom: 1px solid #ebeef5;
    
    .card-title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 8px;
    }
    
    .card-subtitle {
      font-size: 14px;
      color: #606266;
      margin: 0;
      
      .text-danger {
        color: #f56c6c;
        font-weight: 500;
      }
    }
  }
  
  .card-body {
    padding: 20px;
    
    .description-text {
      font-size: 14px;
      color: #606266;
      margin: 0;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .card-header {
      padding: 12px 16px;
      
      .card-title {
        font-size: 16px;
        margin-bottom: 4px;
      }
      
      .card-subtitle {
        font-size: 12px;
      }
    }
    
    .card-body {
      padding: 12px;
      
      .description-text {
        font-size: 12px;
      }
    }
  }
  
  @media (max-width: 480px) {
    .card-header {
      padding: 10px 12px;
    }
    
    .card-body {
      padding: 8px;
    }
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.btn-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
  
  @media (max-width: 480px) {
    .el-button--small {
      padding: 6px 8px;
      font-size: 12px;
    }
  }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ebeef5;
  }
  
  th {
    background-color: #f5f7fa;
    color: #909399;
    font-weight: 600;
    white-space: nowrap;
  }
  
  td {
    color: #606266;
    vertical-align: middle;
  }
  
  tr:hover td {
    background-color: #f5f7fa;
  }
  
  .stopped-row {
    opacity: 0.7;
    background-color: #fafafa;
    
    &:hover td {
      background-color: #f5f5f5;
    }
  }
}

.status-cell {
  max-width: 300px;

  .status-content {
    white-space: normal;
    word-break: break-word;
    
    &.running {
      color: #67c23a;
    }
    
    &.stopped {
      color: #909399;
    }
    
    &.failed {
      color: #f56c6c;
      font-weight: 500;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
}

.action-cell {
  white-space: nowrap;
  
  .action-buttons {
    display: flex;
    gap: 4px;
    flex-wrap: nowrap; // 确保按钮在一行
    justify-content: flex-start;
    
    .el-button {
      flex-shrink: 0; // 防止按钮被压缩
    }
    
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  
  @media (max-width: 768px) {
    .action-buttons {
      justify-content: flex-end;
    }
    
    .el-button--small {
      padding: 4px 8px;
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    .action-buttons {
      gap: 2px;
      
      .el-button--small {
        padding: 4px 6px;
        font-size: 11px;
      }
    }
  }
}

.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
    animation: rotating 2s linear infinite;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 12px;
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  
  .log-stats {
    display: flex;
    align-items: center;
    gap: 24px;
    font-size: 13px;
    color: #606266;
    
    .auto-refresh-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-left: 16px;
      border-left: 1px solid #dcdfe6;
      
      .refresh-label {
        font-size: 12px;
        color: #909399;
      }
      
      .refresh-interval {
        font-size: 12px;
        color: #67c23a;
      }
    }
  }
  
  .log-actions {
    display: flex;
    gap: 8px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    
    .log-stats {
      width: 100%;
      flex-wrap: wrap;
      gap: 12px;
      
      .auto-refresh-control {
        padding-left: 0;
        border-left: none;
      }
    }
    
    .log-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    .log-stats {
      font-size: 12px;
    }
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  .empty-log {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;
    
    .el-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    
    p {
      margin: 0;
      font-size: 14px;
    }
  }
  
  .log-list {
    padding: 12px;
    
    .log-item {
      padding: 8px 12px;
      border-bottom: 1px solid #e4e7ed;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 13px;
      line-height: 1.5;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      
      &:last-child {
        border-bottom: none;
      }
      
      &.log-level-error {
        background-color: #fef0f0;
      }
      
      &.log-level-warn {
        background-color: #fdf6ec;
      }
      
      .log-time {
        color: #909399;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .log-level {
        font-weight: 600;
        font-size: 12px;
        white-space: nowrap;
        flex-shrink: 0;
        
        &.level-error {
          color: #f56c6c;
        }
        
        &.level-warn {
          color: #e6a23c;
        }
        
        &.level-info {
          color: #409eff;
        }
      }
      
      .log-message {
        color: #303133;
        word-break: break-all;
        flex: 1;
      }
    }
  }
  
  @media (max-width: 768px) {
    max-height: 350px;
    
    .log-list {
      padding: 8px;
      
      .log-item {
        font-size: 12px;
        flex-wrap: wrap;
        gap: 4px;
        
        .log-time {
          width: 100%;
          margin-bottom: 2px;
        }
        
        .log-message {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    max-height: 300px;
    
    .log-list {
      .log-item {
        font-size: 11px;
      }
    }
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .el-button {
      width: 100%;
      margin-left: 0 !important;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式处理 */
@media (max-width: 768px) {
  .mapping-table {
    width: 100%;
    min-width: 100%;
    display: block;
    
    thead {
      display: none;
    }
    
    tbody, tr {
      width: 100%;
      display: block;
    }
    
    tr {
      margin-bottom: 16px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background: #fff;
    }
    
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-bottom: 1px solid #ebeef5;
      width: 100%;
      box-sizing: border-box;
      
      &:before {
        content: attr(data-label);
        font-weight: 600;
        color: #909399;
        margin-right: 12px;
        flex-shrink: 0;
        min-width: 80px;
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      &.action-cell {
        padding: 8px 12px;
        
        &:before {
          align-self: flex-start;
          padding-top: 4px;
        }
        
        .action-buttons {
          flex: 1;
          justify-content: flex-end;
        }
      }
    }
  }
  
  .mapping-dialog {
    :deep(.el-dialog) {
      margin: 8px !important;
    }
    
    :deep(.el-dialog__body) {
      padding: 16px;
    }
    
    :deep(.el-dialog__header) {
      padding: 16px;
    }
    
    :deep(.el-dialog__footer) {
      padding: 16px;
    }
  }
}

@media (max-width: 480px) {
  .mapping-table {
    td {
      padding: 6px 8px;
      font-size: 12px;
      
      &:before {
        min-width: 70px;
        font-size: 12px;
      }
      
      &.action-cell {
        .action-buttons {
          .el-button {
            margin-bottom: 0; // 确保没有底部边距
          }
        }
      }
    }
  }
  
  .status-content {
    font-size: 12px;
  }
  
  .btn-group {
    .el-button--small {
      padding: 4px 6px;
      font-size: 11px;
    }
  }
}

/* Element Plus 对话框移动端适配 */
:deep(.el-dialog) {
  @media (max-width: 768px) {
    width: 95% !important;
    margin-top: 5vh !important;
  }
  
  @media (max-width: 480px) {
    width: 100% !important;
    max-height: 100%;
    border-radius: 0;
    
    .el-dialog__header {
      padding: 12px 16px;
    }
    
    .el-dialog__body {
      padding: 16px;
      overflow-y: auto;
      max-height: calc(100vh - 120px);
    }
    
    .el-dialog__footer {
      padding: 12px 16px;
    }
  }
}

/* Element Plus 组件适配 */
:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>